import type { HTMLAttributes, PropsWithChildren } from "react";
import { Children } from "react";

import { css } from "@compiled/react";

import { generateSafeIds } from "@/libraries/design/utils";

import { Adg4TabFC, mapChild } from "./tab-helper";
import { useAdg4TabsContext } from "./tabs";

/**
 * @deprecated - Please use @/libraries/design/patterns/tabs instead
 */
export type Adg4TabPanelProps = PropsWithChildren<{
    forceRender?: boolean;
    keepInDom?: boolean;
    panelIndex?: number;
}> &
    HTMLAttributes<HTMLDivElement>;

const hidePanelStyle = css({
    display: "none"
});

/**
 * @deprecated - Please use @/libraries/design/patterns/tabs instead
 */
const Adg4TabPanel: Adg4TabFC<Adg4TabPanelProps> = ({
    forceRender,
    keepInDom,
    children,
    panelIndex,
    id,
    ...props
}: Adg4TabPanelProps) => {
    const { isCurrentTab } = useAdg4TabsContext();

    const isTabSelected = isCurrentTab(panelIndex);
    const activeClass = isTabSelected ? "active-panel" : "";

    // Check original children count before filtering for role determination
    // Only hide role when there are no children (empty panel)
    // Panels with content should always have role="tabpanel" for accessibility
    const originalChildrenCount = Children.count(children);
    const hideTabPanelRole = !originalChildrenCount;

    children = forceRender || isTabSelected || keepInDom ? children : null;

    const { panelId, tabId } = generateSafeIds(id, panelIndex);

    // When hideTabPanelRole is false, match original structure exactly to avoid snapshot changes
    // Original code overwrote css from props, so we do the same here
    if (!hideTabPanelRole) {
        return (
            <div
                {...props}
                css={[!isTabSelected && !forceRender && hidePanelStyle]}
                id={panelId}
                aria-labelledby={tabId}
                aria-hidden={!isTabSelected}
                role="tabpanel"
                className={["tab-panel", activeClass].join(" ")}
            >
                {Children.map(children, (child) => {
                    return mapChild(child, { isPanelActive: isTabSelected });
                })}
            </div>
        );
    }

    // When hideTabPanelRole is true, omit role and aria-labelledby
    // Extract css separately to preserve it when filtering props
    const {
        "role": _role,
        "aria-labelledby": _ariaLabelledBy,
        "css": existingCss,
        ...filteredProps
    } = props;

    // Build css array - Compiled CSS needs static array literals
    // Always use conditional css (existingCss extracted to prevent conflicts, but not merged)
    const cssProp = [!isTabSelected && !forceRender && hidePanelStyle];

    return (
        <div
            {...filteredProps}
            css={cssProp}
            id={panelId}
            aria-hidden={!isTabSelected}
            className={["tab-panel", activeClass].join(" ")}
        >
            {Children.map(children, (child) => {
                return mapChild(child, { isPanelActive: isTabSelected });
            })}
        </div>
    );
};
Adg4TabPanel.tabsRole = "Adg4TabPanel";

export { Adg4TabPanel };
